import React, {useCallback} from 'react'
import {Keyboard, Pressable, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {StackActions, useNavigation} from '@react-navigation/native'

import {type NavigationProp} from '#/lib/routes/types'
import {isNative} from '#/platform/detection'
import {useProfileShadow} from '#/state/cache/profile-shadow'
import {useMatrixContext} from '#/state/matrix'
import {useProfileBlockMutationQueue} from '#/state/queries/profile'
import * as Toast from '#/view/com/util/Toast'
import {atoms as a, useTheme, type ViewStyleProp} from '#/alf'
import {ArrowBoxLeft_Stroke2_Corner0_Rounded as ArrowBoxLeft} from '#/components/icons/ArrowBoxLeft'
import {DotGrid_Stroke2_Corner0_Rounded as DotsHorizontal} from '#/components/icons/DotGrid'
import {
  Person_Stroke2_Corner0_Rounded as Person,
  PersonCheck_Stroke2_Corner0_Rounded as PersonCheck,
  PersonX_Stroke2_Corner0_Rounded as PersonX,
} from '#/components/icons/Person'
import * as Menu from '#/components/Menu'
import * as Prompt from '#/components/Prompt'
import type * as bsky from '#/types/bsky'

let SecureChatMenu = ({
  roomId,
  profile: initialProfile,
  style,
}: {
  roomId: string
  profile: bsky.profile.AnyProfileView
  style?: ViewStyleProp['style']
}): React.ReactNode => {
  const {_} = useLingui()
  const t = useTheme()
  const profile = useProfileShadow(initialProfile)

  const leaveControl = Prompt.usePromptControl()

  return (
    <>
      <Menu.Root>
        <View style={[style]}>
          <Menu.Trigger label={_(msg`Chat settings`)}>
            {({props, state}) => (
              <Pressable
                {...props}
                onPress={() => {
                  Keyboard.dismiss()
                  props.onPress()
                }}
                style={[
                  a.p_sm,
                  a.rounded_full,
                  (state.hovered || state.pressed) && t.atoms.bg_contrast_25,
                  {marginLeft: -10},
                ]}>
                <DotsHorizontal size="md" style={t.atoms.text} />
              </Pressable>
            )}
          </Menu.Trigger>
        </View>

        <Menu.Outer>
          <SecureChatMenuContent
            profile={profile}
            leaveControl={leaveControl}
          />
        </Menu.Outer>
      </Menu.Root>

      <LeaveSecureChatPrompt control={leaveControl} roomId={roomId} />
    </>
  )
}
SecureChatMenu = React.memo(SecureChatMenu)

function SecureChatMenuContent({
  profile,
  leaveControl,
}: {
  profile: ReturnType<typeof useProfileShadow>
  leaveControl: Prompt.PromptControlProps
}) {
  const navigation = useNavigation<NavigationProp>()
  const {_} = useLingui()

  const onNavigateToProfile = useCallback(() => {
    navigation.navigate('Profile', {name: profile.did})
  }, [navigation, profile.did])

  const isBlocking = !!profile.viewer?.blocking
  const [queueBlock, queueUnblock] = useProfileBlockMutationQueue(profile)

  const toggleBlock = useCallback(() => {
    if (isBlocking) {
      queueUnblock()
    } else {
      queueBlock()
    }
  }, [isBlocking, queueBlock, queueUnblock])

  return (
    <>
      <Menu.Group>
        <Menu.Item
          label={_(msg`Go to user's profile`)}
          onPress={onNavigateToProfile}>
          <Menu.ItemText>
            <Trans>Go to profile</Trans>
          </Menu.ItemText>
          <Menu.ItemIcon icon={Person} />
        </Menu.Item>
      </Menu.Group>
      <Menu.Divider />
      <Menu.Group>
        <Menu.Item
          label={isBlocking ? _(msg`Unblock account`) : _(msg`Block account`)}
          onPress={toggleBlock}>
          <Menu.ItemText>
            {isBlocking ? _(msg`Unblock account`) : _(msg`Block account`)}
          </Menu.ItemText>
          <Menu.ItemIcon icon={isBlocking ? PersonCheck : PersonX} />
        </Menu.Item>
      </Menu.Group>
      <Menu.Divider />
      <Menu.Group>
        <Menu.Item
          label={_(msg`Leave conversation`)}
          onPress={() => leaveControl.open()}>
          <Menu.ItemText>
            <Trans>Leave conversation</Trans>
          </Menu.ItemText>
          <Menu.ItemIcon icon={ArrowBoxLeft} />
        </Menu.Item>
      </Menu.Group>
    </>
  )
}

function LeaveSecureChatPrompt({
  control,
  roomId,
}: {
  control: Prompt.PromptControlProps
  roomId: string
}) {
  const {_} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  const {client} = useMatrixContext()

  const onConfirm = useCallback(async () => {
    if (!client) return
    try {
      await client.leave(roomId)
      await client.forget(roomId)
      navigation.dispatch(
        StackActions.replace('Messages', isNative ? {animation: 'pop'} : {}),
      )
    } catch {
      Toast.show(_(msg`Could not leave chat`), 'xmark')
    }
  }, [client, roomId, navigation, _])

  return (
    <Prompt.Basic
      control={control}
      title={_(msg`Leave conversation`)}
      description={_(
        msg`Are you sure you want to leave this conversation? Your messages will be deleted for you, but not for the other participant.`,
      )}
      confirmButtonCta={_(msg`Leave`)}
      confirmButtonColor="negative"
      onConfirm={onConfirm}
    />
  )
}

export {SecureChatMenu}
